/*********************************************************************************************
 * Sticky cart bar (collection pages) — behavior.
 * Re-renders from the server (Section Rendering API) so cart-derived Liquid data (prices,
 * metafields, etc.) stays correct; never opens the cart drawer on its own.
 */
(function () {
	function init() {
		let bar = document.querySelector('[data-cartbar]');
		if (!bar) return;

		const odometer = typeof window.initNumberOdometer === 'function' ? window.initNumberOdometer() : null;

		function resizeShopifyImage(url, width) {
			if (!url) return url;
			try {
				const parsed = new URL(url, window.location.origin);
				parsed.searchParams.set('width', width);
				return parsed.toString();
			} catch (_) {
				return url;
			}
		}

		// Resolves once the (resized) image is loaded, so it can be painted with zero delay
		// whenever it's actually used.
		function preloadImage(rawSrc) {
			const src = resizeShopifyImage(rawSrc, 80);
			return new Promise((resolve) => {
				const img = new Image();
				img.onload = () => resolve(src);
				img.onerror = () => resolve(src);
				img.src = src;
			});
		}

		let card, toastWrap, toastClose, countBadge, totalEl, remainEl;
		let barShown;

		function collapseToast() {
			if (toastWrap) toastWrap.classList.add('is-collapsed');
		}

		function expandToast() {
			if (toastWrap) toastWrap.classList.remove('is-collapsed');
		}

		// Desktop-only: the bar sits fixed below the sticky header, whose real position
		// depends on whether the announcement bar above it is still in view. Kept in sync
		// via IntersectionObserver below and re-applied whenever the bar is swapped in.
		let announcementVisible = false;

		function applyAnnouncementVisibility() {
			if (bar) bar.classList.toggle('mm-cart-bar--announcement-visible', announcementVisible);
		}

		// Once the footer scrolls into view, the bar fades out (mm-cart-bar--footer-visible)
		// and, once that fade finishes, is fully taken out of the flow with display:none —
		// otherwise, even at opacity 0 with pointer-events off, it still sits in front of
		// footer content on some browsers/inputs.
		let footerVisible = false;

		function applyFooterVisibility() {
			if (!bar) return;
			if (footerVisible) {
				bar.classList.add('mm-cart-bar--footer-visible');
			} else {
				bar.style.display = '';
				bar.classList.remove('mm-cart-bar--footer-visible');
			}
		}

		function onBarTransitionEnd(event) {
			if (event.target !== bar || event.propertyName !== 'opacity') return;
			if (footerVisible) bar.style.display = 'none';
		}

		function bindBar(el) {
			bar = el;
			card = bar.querySelector('.mm-cart-bar-card');
			toastWrap = bar.querySelector('#cbToastWrap');
			toastClose = bar.querySelector('[data-cartbar-toast-close]');
			countBadge = bar.querySelector('#cbCount');
			totalEl = bar.querySelector('#cbTotal');
			remainEl = bar.querySelector('#cbRemain');

			// Bar is already visible on load (cart had items server-side): no entrance animation needed.
			barShown = !bar.hasAttribute('hidden');

			if (toastClose) {
				toastClose.addEventListener('click', collapseToast);
			}

			bar.addEventListener('transitionend', onBarTransitionEnd);

			applyAnnouncementVisibility();
			applyFooterVisibility();
		}

		bindBar(bar);

		const announcementBar = document.querySelector('.mm-section-announcement-bar');
		if (announcementBar) {
			const announcementObserver = new IntersectionObserver((entries) => {
				announcementVisible = entries[entries.length - 1].isIntersecting;
				applyAnnouncementVisibility();
			}, { threshold: 0 });
			announcementObserver.observe(announcementBar);
		}

		const footerEl = document.querySelector('.mm-footer-container');
		if (footerEl) {
			const footerObserver = new IntersectionObserver((entries) => {
				footerVisible = entries[entries.length - 1].isIntersecting;
				applyFooterVisibility();
			}, { threshold: 0 });
			footerObserver.observe(footerEl);
		}

		function onScroll() {
			if (window.scrollY > 24) collapseToast();
		}
		window.addEventListener('scroll', onScroll, { passive: true });

		function popBadge() {
			if (!window.gsap || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
			window.gsap.fromTo(countBadge, { scale: 1.45 }, { scale: 1, duration: 0.4, ease: 'back.out(2.5)' });
		}

		function animateEntrance() {
			if (!window.gsap || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
			window.gsap.fromTo(card, { opacity: 0, y: 100 }, { opacity: 1, y: 0, duration: 0.55, ease: 'power3.out' });
		}

		let refreshSeq = 0;

		function refreshFromServer(justAdded, { image, cartBarHtml } = {}) {
			const seq = ++refreshSeq;

			const oldItemCount = parseInt(countBadge.textContent, 10) || 0;
			const wasShown = barShown;
			const oldTotalText = totalEl ? totalEl.textContent : undefined;
			const oldRemainText = remainEl ? remainEl.textContent : undefined;

			// If the caller already has the cart bar's HTML from the same request that performed
			// the mutation (cart/add.js ?sections=mm-cart-bar), use it directly: it's guaranteed
			// fresh and consistent, with no extra round-trip and no risk of a stale/racy GET.
			const htmlPromise = cartBarHtml
				? Promise.resolve(cartBarHtml)
				: (() => {
					const url = new URL(window.location.href);
					url.searchParams.set('section_id', 'mm-cart-bar');
					return fetch(url.toString(), { cache: 'no-store' }).then((res) => res.text());
				})();

			// Preload in parallel with the HTML fetch so the correct thumb can be painted on the
			// very first frame the new bar appears — the server-rendered thumb (based on
			// cart.items' last line) isn't a reliable source for "the variant just added", but
			// swapping it in first and correcting it after causes a visible flash of the wrong
			// image.
			const imagePromise = image ? preloadImage(image) : Promise.resolve(null);

			Promise.all([htmlPromise, imagePromise]).then(([html, preloadedImageSrc]) => {
				// Discard out-of-order responses: a newer request may have already resolved.
				if (seq !== refreshSeq) return;

				const doc = new DOMParser().parseFromString(html, 'text/html');
				const newBar = doc.querySelector('[data-cartbar]');
				if (!newBar) return;

				const newItemCount = parseInt(newBar.querySelector('#cbCount')?.textContent, 10) || 0;
				const newTotalEl = newBar.querySelector('#cbTotal');
				const newRemainEl = newBar.querySelector('#cbRemain');

				// Seed the odometer's baseline on the fresh nodes so the swap-in still animates
				// from the previous value instead of popping straight to the new one.
				if (odometer) {
					if (newTotalEl && oldTotalText !== undefined) newTotalEl.dataset.odometerValue = oldTotalText;
					if (newRemainEl && oldRemainText !== undefined) newRemainEl.dataset.odometerValue = oldRemainText;
				}

				if (preloadedImageSrc) {
					const newThumbImg = newBar.querySelector('#cbThumbImg');
					if (newThumbImg) newThumbImg.src = preloadedImageSrc;
				}

				bar.replaceWith(newBar);
				bindBar(newBar);

				const willShow = !bar.hasAttribute('hidden');
				if (willShow && !wasShown) animateEntrance();
				if (willShow && newItemCount !== oldItemCount) popBadge();

				if (odometer) {
					if (totalEl) odometer(totalEl, totalEl.textContent);
					if (remainEl) odometer(remainEl, remainEl.textContent);
				}

				if (justAdded) expandToast();
			});
		}

		// Add-to-cart flows dispatch 'cartbar:add' (with the accurate variant image/HTML) and
		// then, as a side effect of their own drawer refresh, a plain 'cart:updated' right after.
		// That follow-up carries no useful data and would re-render the bar from a stale/generic
		// server response, undoing what the add's own refresh just applied (thumb image, toast).
		// Skip it when it's clearly just the redundant tail of an add we already handled.
		let lastAddAt = 0;

		document.addEventListener('cartbar:add', (event) => {
			lastAddAt = Date.now();
			refreshFromServer(true, event.detail || {});
		});

		document.addEventListener('cart:updated', () => {
			if (Date.now() - lastAddAt < 1500) return;
			refreshFromServer(false);
		});

		// Punch feedback on quick-add buttons ("+" on product cards).
		document.addEventListener('click', (event) => {
			const btn = event.target.closest('.mm-product-card-cta, [data-quick-add-quantity], [data-cart-plus]');
			if (!btn || !window.gsap || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
			window.gsap.fromTo(btn, { scale: 0.82 }, { scale: 1, duration: 0.35, ease: 'back.out(3)' });
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init, { once: true });
	} else {
		init();
	}
})();
